import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { RichTextEditor } from '@/components/rich-text';
import { ReadOnlyContentDisplay } from '@/components/read-only-content-display';
import { cn } from '@/lib/utils';
import { Check, Edit, Loader2, X } from 'lucide-react';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { toast } from 'sonner';

interface EditableRichInfoCardProps {
  title: string;
  subtitle?: string;
  icon: ReactNode;
  value?: string | null;
  placeholder: string;
  canEdit: boolean;
  onSave: (nextValue: string) => Promise<void>;
  onUploadFile?: (file: File) => Promise<string>;
}

export const EditableRichInfoCard = ({
  title,
  subtitle,
  icon,
  value,
  placeholder,
  canEdit,
  onSave,
  onUploadFile,
}: EditableRichInfoCardProps) => {
  const [draft, setDraft] = useState(value ?? '');
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const htmlGetterRef = useRef<(() => string) | null>(null);
  const serializedGetterRef = useRef<(() => string) | null>(null);

  useEffect(() => {
    if (!isEditing) {
      setDraft(value ?? '');
    }
  }, [value, isEditing]);

  const handleCancel = () => {
    setDraft(value ?? '');
    setIsEditing(false);
  };

  const handleSave = async () => {
    const latestContent =
      serializedGetterRef.current?.() || htmlGetterRef.current?.() || draft;
    setIsSaving(true);
    try {
      await onSave(latestContent);
      setDraft(latestContent);
      setIsEditing(false);
    } catch {
      toast.error('Failed to update');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <section className="rounded-xl border bg-card p-4 shadow-sm sm:p-5">
      <div className="mb-3 flex items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
            {icon}
          </div>
          <div className="min-w-0">
            <p className="truncate text-sm font-medium text-foreground sm:text-base">
              {title}
            </p>
            {subtitle ? (
              <p className="truncate text-xs text-muted-foreground sm:text-sm">{subtitle}</p>
            ) : null}
          </div>
        </div>

        {canEdit ? (
          <div className="flex shrink-0 items-center gap-1.5">
            {isEditing ? (
              <>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  className="h-8 gap-1.5 px-2 text-muted-foreground hover:text-foreground sm:px-3"
                  onClick={handleCancel}
                  disabled={isSaving}
                >
                  <X className="h-4 w-4" />
                  <span className="hidden sm:inline">Cancel</span>
                </Button>
                <Button
                  type="button"
                  size="sm"
                  className="h-8 gap-1.5 px-2 sm:px-3"
                  onClick={() => {
                    void handleSave();
                  }}
                  disabled={isSaving}
                >
                  {isSaving ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      <span className="hidden sm:inline">Saving</span>
                    </>
                  ) : (
                    <>
                      <Check className="h-4 w-4" />
                      <span className="hidden sm:inline">Save</span>
                    </>
                  )}
                </Button>
              </>
            ) : (
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="h-8 gap-1.5 bg-transparent px-2 sm:px-3"
                onClick={() => setIsEditing(true)}
              >
                <Edit className="h-4 w-4" />
                <span className="hidden sm:inline">Edit</span>
              </Button>
            )}
          </div>
        ) : null}
      </div>

      {isEditing ? (
        <RichTextEditor
          value={draft}
          onChange={setDraft}
          placeholder={placeholder}
          minHeight={220}
          disabled={isSaving}
          onUploadFile={onUploadFile}
          onProvideGetHtml={(getHtml) => {
            htmlGetterRef.current = getHtml;
          }}
          onProvideGetSerializedState={(getSerializedState) => {
            serializedGetterRef.current = getSerializedState;
          }}
        />
      ) : (
        <div
          className={cn(
            'min-h-[100px] py-1',
            canEdit && 'cursor-text select-text'
          )}
          onDoubleClick={() => {
            if (canEdit) setIsEditing(true);
          }}
          role={canEdit ? 'button' : undefined}
          tabIndex={canEdit ? 0 : undefined}
          onKeyDown={(event) => {
            if (!canEdit) return;
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              setIsEditing(true);
            }
          }}
        >
          {draft?.trim() ? (
            <ReadOnlyContentDisplay html={draft} />
          ) : (
            <p className="text-sm italic text-muted-foreground/60">{placeholder}</p>
          )}
        </div>
      )}
    </section>
  );
};

interface EditableTextInfoCardProps {
  title: string;
  subtitle?: string;
  icon: ReactNode;
  value?: string | null;
  placeholder: string;
  canEdit: boolean;
  onSave: (nextValue: string) => Promise<void>;
}

export const EditableTextInfoCard = ({
  title,
  subtitle,
  icon,
  value,
  placeholder,
  canEdit,
  onSave,
}: EditableTextInfoCardProps) => {
  const [draft, setDraft] = useState(value ?? '');
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!isEditing) {
      setDraft(value ?? '');
    }
  }, [value, isEditing]);

  const handleCancel = () => {
    setDraft(value ?? '');
    setIsEditing(false);
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await onSave(draft);
      setIsEditing(false);
    } catch {
      toast.error('Failed to update');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <section className="rounded-xl border bg-card p-4 shadow-sm sm:p-5">
      <div className="mb-3 flex items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
            {icon}
          </div>
          <div className="min-w-0">
            <p className="truncate text-sm font-medium text-foreground sm:text-base">
              {title}
            </p>
            {subtitle ? (
              <p className="truncate text-xs text-muted-foreground sm:text-sm">{subtitle}</p>
            ) : null}
          </div>
        </div>

        {canEdit ? (
          <div className="flex shrink-0 items-center gap-1.5">
            {isEditing ? (
              <>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  className="h-8 gap-1.5 px-2 text-muted-foreground hover:text-foreground sm:px-3"
                  onClick={handleCancel}
                  disabled={isSaving}
                >
                  <X className="h-4 w-4" />
                  <span className="hidden sm:inline">Cancel</span>
                </Button>
                <Button
                  type="button"
                  size="sm"
                  className="h-8 gap-1.5 px-2 sm:px-3"
                  onClick={() => {
                    void handleSave();
                  }}
                  disabled={isSaving}
                >
                  {isSaving ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      <span className="hidden sm:inline">Saving</span>
                    </>
                  ) : (
                    <>
                      <Check className="h-4 w-4" />
                      <span className="hidden sm:inline">Save</span>
                    </>
                  )}
                </Button>
              </>
            ) : (
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="h-8 gap-1.5 bg-transparent px-2 sm:px-3"
                onClick={() => setIsEditing(true)}
              >
                <Edit className="h-4 w-4" />
                <span className="hidden sm:inline">Edit</span>
              </Button>
            )}
          </div>
        ) : null}
      </div>

      {isEditing ? (
        <Input
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          disabled={isSaving}
          placeholder={placeholder}
        />
      ) : (
        <div
          className={cn(
            'rounded-xl border border-border/40 bg-muted/30 px-4 py-3 text-sm text-foreground sm:px-5 sm:py-4 sm:text-base',
            canEdit && 'cursor-text select-text hover:border-border/60 hover:bg-muted/40'
          )}
          onDoubleClick={() => {
            if (canEdit) setIsEditing(true);
          }}
          role={canEdit ? 'button' : undefined}
          tabIndex={canEdit ? 0 : undefined}
          onKeyDown={(event) => {
            if (!canEdit) return;
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              setIsEditing(true);
            }
          }}
        >
          {draft?.trim() || <span className="italic text-muted-foreground/60">{placeholder}</span>}
        </div>
      )}
    </section>
  );
};
